import { type RequestStatus, RequestStatusSchema } from '@gestion-permisos/contracts';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { type ChoiceOption, ChoiceList } from '../components/choice-list.js';
import { REQUEST_STATUSES, STATUS_LABELS } from './format.js';

export interface ListFilters {
  readonly status: RequestStatus | undefined;
  readonly page: number;
}

/** Página de un parámetro de la URL: un entero positivo; cualquier otro valor es la página 1. */
export function pageFrom(params: URLSearchParams, name: string): number {
  const page = Number(params.get(name));
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

/**
 * Filtro y página de un listado de solicitudes, en la URL (`?estado=` y `?pagina=`), sin almacenamiento del
 * navegador. `estado` solo admite un estado del contrato; lo demás se ignora. Cambiar el filtro vuelve a la
 * página 1 y **conserva los demás parámetros** (p. ej., la página del equipo en `/mi-equipo`, W5).
 */
/** Búsqueda, fechas civiles, tipo, área y orden, tal como están en la URL (sin convertir con `Date`). */
export interface RequestQueryFilters {
  readonly q: string;
  readonly from: string;
  readonly to: string;
  readonly permissionTypeId: string;
  readonly areaId: string;
  readonly sort: string;
}

const QUERY_PARAMS: Record<keyof RequestQueryFilters, string> = {
  q: 'q',
  from: 'desde',
  to: 'hasta',
  permissionTypeId: 'tipo',
  areaId: 'area',
  sort: 'orden',
};

/**
 * Filtros de H-02 en la URL. Aplicarlos borra `?pagina=` y conserva el resto (estado, página del equipo).
 * Un valor vacío no se escribe. La API valida las fechas (400 si «desde» es posterior a «hasta»).
 */
export function useRequestQueryFilters(): [RequestQueryFilters, (next: RequestQueryFilters) => void] {
  const [params, setParams] = useSearchParams();
  const value = (key: keyof RequestQueryFilters) => params.get(QUERY_PARAMS[key]) ?? '';
  const filters: RequestQueryFilters = {
    q: value('q'),
    from: value('from'),
    to: value('to'),
    permissionTypeId: value('permissionTypeId'),
    areaId: value('areaId'),
    sort: value('sort'),
  };
  const apply = (next: RequestQueryFilters) =>
    setParams((current) => {
      const updated = new URLSearchParams(current);
      updated.delete('pagina');
      for (const key of Object.keys(QUERY_PARAMS) as (keyof RequestQueryFilters)[]) {
        const text = next[key].trim();
        if (text === '') updated.delete(QUERY_PARAMS[key]);
        else updated.set(QUERY_PARAMS[key], text);
      }
      return updated;
    });
  return [filters, apply];
}

export function useListFilters(): [ListFilters, (status: RequestStatus | undefined) => void] {
  const [params, setParams] = useSearchParams();
  const status = RequestStatusSchema.safeParse(params.get('estado'));
  const filters = { status: status.success ? status.data : undefined, page: pageFrom(params, 'pagina') };
  const setStatus = (next: RequestStatus | undefined) =>
    setParams((current) => {
      const updated = new URLSearchParams(current);
      updated.delete('pagina');
      if (next === undefined) updated.delete('estado');
      else updated.set('estado', next);
      return updated;
    });
  return [filters, setStatus];
}

/**
 * Enlaces a otra página de un listado: cambia solo `param` y conserva el resto de la URL actual. La página
 * 1 no se escribe en la URL.
 */
export function usePageHref(param: string): (page: number) => string {
  const location = useLocation();
  return (page) => {
    const params = new URLSearchParams(location.search);
    if (page > 1) params.set(param, String(page));
    else params.delete(param);
    const query = params.toString();
    return `${location.pathname}${query ? `?${query}` : ''}`;
  };
}

/** «Todos» (vacío) y los estados del contrato, con su texto. */
export const STATUS_OPTIONS: readonly ChoiceOption[] = [{ value: '', label: 'Todos' }, ...REQUEST_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] }))];

/** `className` sustituye el margen superior por defecto (`mt-4`), p. ej. dentro de una tarjeta. */
export function StatusFilter({
  status,
  onChange,
  className = 'mt-4',
}: {
  status: RequestStatus | undefined;
  onChange: (status: RequestStatus | undefined) => void;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor="filtro-estado" className="mr-2 text-sm text-muted-foreground">
        Estado
      </label>
      <ChoiceList
        id="filtro-estado"
        inline
        value={status ?? ''}
        options={STATUS_OPTIONS}
        onChange={(value) => {
          const next = RequestStatusSchema.safeParse(value);
          onChange(next.success ? next.data : undefined);
        }}
        className="rounded-md border border-input bg-card px-2 py-1 text-sm"
      />
    </div>
  );
}

const SOLICITUDES: [singular: string, plural: string] = ['solicitud', 'solicitudes'];

/** «Página X de Y · N solicitudes», con Anterior y Siguiente. */
export function Pagination({
  page,
  total,
  pageSize,
  hrefFor,
  label = 'Paginación',
  units = SOLICITUDES,
}: {
  page: number;
  total: number;
  pageSize: number;
  hrefFor: (page: number) => string;
  label?: string;
  units?: [singular: string, plural: string];
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <nav aria-label={label} className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
      <span>
        Página {page} de {pages} · {total} {total === 1 ? units[0] : units[1]}
      </span>
      <span className="flex gap-2">
        {page > 1 ? (
          <Link to={hrefFor(page - 1)} className="rounded-md border border-border bg-card px-3 py-1 hover:bg-muted">
            Anterior
          </Link>
        ) : null}
        {page < pages ? (
          <Link to={hrefFor(page + 1)} className="rounded-md border border-border bg-card px-3 py-1 hover:bg-muted">
            Siguiente
          </Link>
        ) : null}
      </span>
    </nav>
  );
}

/** Una página más allá del final: no tiene filas, pero sí un enlace a la primera. */
export function PastLastPage({ firstPage, what = 'solicitudes' }: { firstPage: string; what?: string }) {
  return (
    <p className="text-muted-foreground">
      Esta página no tiene {what}.{' '}
      <Link to={firstPage} className="underline">
        Ir a la primera página
      </Link>
    </p>
  );
}

export const TABLE = 'w-full text-left text-sm';
export const TABLE_WRAPPER = 'overflow-x-auto rounded-md border border-border bg-card';
export const TH = 'px-3 py-2 font-medium';
export const TD = 'px-3 py-2';
